import { useState } from "react";
import { toast } from "sonner";
import { HeartPulse, LogOut, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { loadRazorpay } from "@/lib/razorpay";
import Pricing from "@/components/Pricing";
import Footer from "@/components/Footer";

export default function Paywall({ onActivated }) {
  const { user, logout } = useAuth();
  const [busyPlan, setBusyPlan] = useState(null);

  const startCheckout = async (planId) => {
    setBusyPlan(planId);
    try {
      await loadRazorpay();
      const { data } = await api.post("/billing/subscribe", { plan: planId });
      const rzp = new window.Razorpay({
        key: data.key_id,
        subscription_id: data.subscription_id,
        name: "MDvia Care",
        description: `${planId.charAt(0).toUpperCase() + planId.slice(1)} subscription`,
        theme: { color: "#1E5AA8" },
        prefill: { email: user?.email || "", name: user?.full_name || user?.name || "" },
        handler: async (resp) => {
          try {
            await api.post("/billing/verify", {
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_subscription_id: resp.razorpay_subscription_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("Subscription active — welcome to MDvia Care Premium!");
            onActivated?.();
          } catch {
            toast.error("We couldn't confirm your payment. Please contact support@mdviacare.com.");
          } finally {
            setBusyPlan(null);
          }
        },
        modal: { ondismiss: () => setBusyPlan(null) },
      });
      rzp.on("payment.failed", () => { toast.error("Payment failed. Please try again."); setBusyPlan(null); });
      rzp.open();
    } catch (e) {
      toast.error("Could not start checkout: " + (e.response?.data?.detail || e.message));
      setBusyPlan(null);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#FBFBF7]" data-testid="paywall">
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-amber-100/70 shadow-sm">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-[#1E5AA8] flex items-center justify-center shadow-md">
              <HeartPulse className="w-5 h-5 text-amber-300" />
            </div>
            <div>
              <h1 className="font-display text-lg font-bold text-[#123C73] leading-none">MDvia Care</h1>
              <p className="text-[11px] text-slate-500 mt-0.5">{user?.full_name || user?.name || user?.email}</p>
            </div>
          </div>
          <button data-testid="paywall-logout" onClick={logout}
            className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-red-600 px-3 py-2 rounded-xl hover:bg-red-50">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </header>

      <main className="flex-1">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 pt-10 text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-xs font-semibold px-3 py-1">
            <ShieldCheck className="w-4 h-4" /> Activate your account
          </span>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mt-4">
            Subscribe to unlock MDvia Care
          </h2>
          <p className="text-slate-500 mt-3 max-w-xl mx-auto text-sm sm:text-base">
            Choose a plan to start recording consultations, drafting AI prescriptions and managing your clinic. Secure autopay via Razorpay — cancel anytime.
          </p>
        </div>

        <Pricing onChoose={startCheckout} busyPlan={busyPlan} forceCurrency="INR" />
      </main>

      <Footer />
    </div>
  );
}
